<template>
  <div>
    <div v-if="activityStatus == 'new' || activityStatus == 'edit'">
      <base-header class="pb-6 bg-primary">
        <b-row class="align-items-center py-4">
          <b-col lg="6" cols="7">
            <h6 class="h2 text-white d-inline-block mb-0">
              Finance Networks
            </h6>
          </b-col>
        </b-row>
      </base-header>
      <div class="container-fluid mt--6">
        <validation-observer v-slot="{ handleSubmit }" ref="formValidator">
          <form
            class="needs-validation"
            @submit.prevent="handleSubmit(saveChanges)"
          >
            <b-card no-body class="mb-4">
              <b-card-header>
                <h3 class="mb-0">
                  {{
                    activityStatus == "new"
                      ? "Create finance network"
                      : "Editing: " + model.name
                  }}
                </h3>
              </b-card-header>
              <b-card-body>
                <b-row>
                  <b-col md="6">
                    <base-input
                      name="Network Name"
                      label="Network Name"
                      v-model="model.name"
                      placeholder="e.g. Brokers Mutual network"
                      rules="required"
                    />
                  </b-col>
                </b-row>

                <b-row class="mt-2">
                  <b-col>
                    <h4 class="mb-1">Insurance broker by product</h4>
                    <p class="text-muted small mb-3">
                      Optional configuration for clients: which insurance broker
                      places each product for this network (e.g. Motor →
                      Transport Protect, Plant &amp; Equipment → Advisernet).
                      Does not change who can list network quotes.
                    </p>
                    <b-alert
                      v-if="legacyInsuranceBrokerId"
                      show
                      variant="warning"
                      class="py-2"
                    >
                      This network previously had a single parent insurance
                      broker
                      <strong>{{ insuranceBrokerName(legacyInsuranceBrokerId) }}</strong>.
                      Assign product-level brokers below; saving replaces the
                      legacy field.
                    </b-alert>
                  </b-col>
                </b-row>

                <b-row
                  v-for="product in productList"
                  :key="product.Product_code"
                  class="mb-2 align-items-end"
                >
                  <b-col md="5">
                    <base-input
                      :name="'Product ' + product.Product_code"
                      :label="product.Product_name || product.Product_code"
                    >
                      <el-input
                        :value="product.Product_code"
                        disabled
                        class="w-100"
                      />
                    </base-input>
                  </b-col>
                  <b-col md="7">
                    <base-input
                      :name="'IB ' + product.Product_code"
                      label="Insurance broker"
                    >
                      <el-select
                        v-model="model.productInsuranceBrokers[product.Product_code]"
                        filterable
                        clearable
                        placeholder="Select insurance broker (optional)"
                        class="w-100"
                      >
                        <el-option
                          v-for="item in insuranceBrokerList"
                          :key="item.insurance_broker_id"
                          :label="item.insurance_broker_name"
                          :value="item.insurance_broker_id"
                        />
                      </el-select>
                    </base-input>
                  </b-col>
                </b-row>

                <b-row v-if="!productList.length">
                  <b-col>
                    <p class="text-muted small">
                      No products found. Product mappings can be added once
                      yiProduct records exist.
                    </p>
                  </b-col>
                </b-row>

                <b-row class="mt-3">
                  <b-col>
                    <base-button type="primary" native-type="submit">
                      Save
                    </base-button>
                    <base-button type="secondary" class="ml-2" @click="cancelEdit">
                      Cancel
                    </base-button>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>
          </form>
        </validation-observer>
      </div>
    </div>

    <div v-else>
      <base-header class="pb-6">
        <b-row align-v="center" class="py-4">
          <b-col cols="7" lg="6">
            <h6 class="h1 text-white d-inline-block mb-0">Finance Networks</h6>
          </b-col>
          <b-col lg="6" cols="5" class="text-right">
            <base-button size="sm" type="primary" @click="addNetwork">
              <i class="fas fa-plus pr-2"></i>New Network
            </base-button>
          </b-col>
        </b-row>
      </base-header>
      <div class="container-fluid mt--6">
        <card body-classes="px-0 pb-1" footer-classes="pb-2" class="finance-networks-table-card">
          <el-table
            :data="tableData"
            row-key="finance_network_id"
            header-row-class-name="thead-light"
            v-loading="loading"
            class="finance-networks-table"
          >
            <el-table-column min-width="80px" label="Edit">
              <template slot-scope="{ row }">
                <base-button
                  @click.native="populateForm(row)"
                  class="edit"
                  type="primary"
                  size="sm"
                  icon
                >
                  <i class="fas fa-edit"></i>
                </base-button>
              </template>
            </el-table-column>
            <el-table-column
              label="Network ID"
              prop="finance_network_id"
              min-width="140px"
            />
            <el-table-column label="Name" prop="name" min-width="200px" />
            <el-table-column
              label="Insurance broker by product"
              min-width="320px"
            >
              <template slot-scope="{ row }">
                <div
                  v-for="line in productMappingLines(row)"
                  :key="line"
                  class="product-ib-line"
                >
                  {{ line }}
                </div>
                <span v-if="!productMappingLines(row).length">—</span>
              </template>
            </el-table-column>
            <template #empty>
              <p>{{ loading ? "" : "No finance networks yet" }}</p>
            </template>
          </el-table>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Input } from "element-ui";
import { appFunctions } from "../../helpers/appFunctions";
import { toasts } from "../../helpers/toasts";

function emptyProductMap(productList) {
  const map = {};
  (productList || []).forEach(p => {
    if (p && p.Product_code) {
      map[p.Product_code] = null;
    }
  });
  return map;
}

export default {
  name: "FinanceNetworks",
  data() {
    return {
      loading: false,
      activityStatus: "list",
      tableData: [],
      insuranceBrokerList: [],
      productList: [],
      currentNetworkId: "",
      legacyInsuranceBrokerId: null,
      model: {
        name: "",
        productInsuranceBrokers: {}
      }
    };
  },
  methods: {
    insuranceBrokerName(id) {
      if (!id) {
        return "—";
      }
      const found = this.insuranceBrokerList.find(
        item => item.insurance_broker_id === id
      );
      return found ? found.insurance_broker_name : id;
    },
    productName(productCode) {
      const found = this.productList.find(p => p.Product_code === productCode);
      return found ? found.Product_name || productCode : productCode;
    },
    productMappingLines(row) {
      const lines = [];
      const mappings = Array.isArray(row.product_insurance_brokers)
        ? row.product_insurance_brokers
        : [];
      mappings.forEach(m => {
        if (!m || !m.Product_code || !m.insurance_broker_id) {
          return;
        }
        lines.push(
          `${this.productName(m.Product_code)} → ${this.insuranceBrokerName(
            m.insurance_broker_id
          )}`
        );
      });
      // Legacy single-parent records (pre product map)
      if (!lines.length && row.insurance_broker_id) {
        lines.push(
          `(legacy) → ${this.insuranceBrokerName(row.insurance_broker_id)}`
        );
      }
      return lines;
    },
    buildPayloadMappings() {
      return Object.keys(this.model.productInsuranceBrokers || {})
        .filter(code => this.model.productInsuranceBrokers[code])
        .map(Product_code => ({
          Product_code,
          insurance_broker_id: this.model.productInsuranceBrokers[Product_code]
        }));
    },
    addNetwork() {
      this.activityStatus = "new";
      this.currentNetworkId = "";
      this.legacyInsuranceBrokerId = null;
      this.model.name = "";
      this.model.productInsuranceBrokers = emptyProductMap(this.productList);
    },
    cancelEdit() {
      this.activityStatus = "list";
      this.legacyInsuranceBrokerId = null;
      this.getNetworks();
    },
    populateForm(row) {
      this.activityStatus = "edit";
      this.currentNetworkId = row.finance_network_id;
      this.model.name = row.name;
      const map = emptyProductMap(this.productList);
      const mappings = Array.isArray(row.product_insurance_brokers)
        ? row.product_insurance_brokers
        : [];
      mappings.forEach(m => {
        if (m && m.Product_code && m.insurance_broker_id) {
          map[m.Product_code] = m.insurance_broker_id;
        }
      });
      this.model.productInsuranceBrokers = map;
      const hasProductMaps = mappings.some(
        m => m && m.Product_code && m.insurance_broker_id
      );
      this.legacyInsuranceBrokerId =
        !hasProductMaps && row.insurance_broker_id
          ? row.insurance_broker_id
          : null;
    },
    async getNetworks() {
      this.loading = true;
      try {
        const res = await appFunctions.crud_financeNetwork("list", {});
        this.tableData = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    async getInsuranceBrokers() {
      const res = await appFunctions.crud_insuranceBroker("list");
      this.insuranceBrokerList = res.data || [];
    },
    async getProducts() {
      const res = await appFunctions.crud_yiProduct("getAllProducts", {});
      const products = res.data || [];
      // Stable sort: known live products first, then name
      const priority = {
        "YIHQ-MOTORLIVE": 1,
        "YIHQ-PSLIVE": 2
      };
      this.productList = products.slice().sort((a, b) => {
        const pa = priority[a.Product_code] || 50;
        const pb = priority[b.Product_code] || 50;
        if (pa !== pb) {
          return pa - pb;
        }
        return String(a.Product_name || a.Product_code).localeCompare(
          String(b.Product_name || b.Product_code)
        );
      });
    },
    async saveChanges() {
      const isValid = await this.$refs.formValidator.validate();
      if (!isValid) {
        toasts.error("Please complete the required fields before proceeding.");
        return;
      }

      const payload = {
        name: this.model.name,
        product_insurance_brokers: this.buildPayloadMappings()
      };

      if (this.currentNetworkId) {
        await appFunctions.crud_financeNetwork("update", {
          finance_network_id: this.currentNetworkId,
          ...payload
        });
      } else {
        const idRes = await appFunctions.crud_financeNetwork(
          "getNextFinanceNetworkId",
          {}
        );
        await appFunctions.crud_financeNetwork("create", {
          finance_network_id: idRes.data,
          ...payload
        });
      }

      toasts.success("Finance network saved");
      this.activityStatus = "list";
      await this.getNetworks();
    }
  },
  async mounted() {
    await Promise.all([this.getInsuranceBrokers(), this.getProducts()]);
    this.model.productInsuranceBrokers = emptyProductMap(this.productList);
    await this.getNetworks();
  },
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  }
};
</script>

<style scoped>
.finance-networks-table-card {
  background: #fff;
}

.finance-networks-table,
.finance-networks-table >>> .el-table,
.finance-networks-table >>> .el-table__body-wrapper,
.finance-networks-table >>> .el-table__header-wrapper,
.finance-networks-table >>> .el-table tr,
.finance-networks-table >>> .el-table th,
.finance-networks-table >>> .el-table td {
  background-color: #fff !important;
}

.product-ib-line {
  line-height: 1.4;
  margin-bottom: 2px;
}

.w-100 {
  width: 100%;
}
</style>
